"use client";

import React, { useEffect, useRef, useState } from 'react';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
export function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const statsData = [{
  id: 1,
  value: "50+",
  label: "MARKETS COVERED",
  parallaxSpeed: 0.15,
  floatDelay: "0s",
  colorClass: "text-[#3CC5F3]",
  glowClass: "from-[#3CC5F3]/20 to-transparent"
}, {
  id: 2,
  value: "24/7",
  label: "AI ASSISTANCE",
  parallaxSpeed: 0.25,
  floatDelay: "-2s",
  colorClass: "text-white",
  glowClass: "from-white/10 to-transparent"
}, {
  id: 3,
  value: "$0",
  label: "TENANT FEE",
  parallaxSpeed: 0.1,
  floatDelay: "-4s",
  colorClass: "text-[#FCB231]",
  glowClass: "from-[#FCB231]/20 to-transparent"
}];
export default function ImpactInMotion() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const sphereRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    let animationFrameId: number;
    const handleScroll = () => {
      animationFrameId = requestAnimationFrame(() => {
        sphereRefs.current.forEach((el, index) => {
          if (el && el.parentElement) {
            const rect = el.parentElement.getBoundingClientRect();
            const distanceFromCenter = window.innerHeight / 2 - (rect.top + rect.height / 2);
            const speed = statsData[index].parallaxSpeed;
            const yOffset = distanceFromCenter * speed;
            el.style.transform = `translateY(${yOffset}px)`;
          }
        });
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <section data-section-id="134619" id="by-the-numbers" className="dark bg-[#0D1B2A] text-white relative overflow-hidden py-32 lg:py-48" ref={sectionRef}>
            {}
            <style dangerouslySetInnerHTML={{
      __html: `
                @keyframes float-sphere {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }
                .animate-float-custom {
                    animation: float-sphere 6s ease-in-out infinite;
                }
            `
    }} />

            {}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3CC5F3]/10 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#FCB231]/10 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className={`text-center mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-6xl font-sans font-black tracking-tighter mb-4 uppercase">
                        By The <span className="text-[#3CC5F3]">Numbers</span>
                    </h2>
                    <p className="text-white/60 max-w-2xl mx-auto text-lg font-sans font-medium">
                        Real estate at the speed of AI. We optimize every metric to ensure you find the perfect industrial or office space with zero friction.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8">
                    {statsData.map((stat, i) => <div key={stat.id} data-index={i} className={`relative flex flex-col items-center justify-center h-[300px] md:h-[400px] transition-all duration-1000 delay-[${i * 200}ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
                            {}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" ref={el => {
            sphereRefs.current[i] = el;
          }}>
                                {}
                                <div className="animate-float-custom w-56 h-56 md:w-72 md:h-72 rounded-full relative overflow-hidden" style={{
              animationDelay: stat.floatDelay
            }}>
                                    {}
                                    <div className="absolute inset-0 backdrop-blur-[30px] bg-white/[0.01] border border-white/[0.05] rounded-full shadow-[inset_0_0_40px_rgba(255,255,255,0.02),0_20px_40px_rgba(0,0,0,0.6)]" />
                                    
                                    {}
                                    <div className={`absolute inset-0 rounded-full bg-gradient-to-b ${stat.glowClass} opacity-40`} />
                                    
                                    {}
                                    <div className="absolute inset-[1px] rounded-full border border-white/5 mix-blend-overlay" />
                                </div>
                            </div>

                            {}
                            <div className="relative z-10 text-center flex flex-col items-center justify-center">
                                <span className={`text-7xl md:text-8xl lg:text-[120pt] font-sans font-black tracking-tighter ${stat.colorClass} drop-shadow-[0_0_40px_rgba(60,197,243,0.15)]`}>
                                    {stat.value}
                                </span>
                                <span className="mt-6 text-xs md:text-sm font-black tracking-[0.3em] uppercase text-white bg-[#0D1B2A]/80 backdrop-blur-md px-6 py-2 rounded-none border border-white/10">
                                    {stat.label}
                                </span>
                            </div>
                        </div>)}
                </div>
            </div>
        </section>;
}